import React, { useEffect, useRef, useState } from 'react';
import { getYouTubeEmbedUrl, getYouTubeShortsUrl, getYouTubeThumbnail, getYouTubeVideoId } from '../../../utils/helpers';
import Icon from '../../common/Icon/Icon';

/**
 * Inline Video Card Component
 * Plays YouTube videos directly in the card without external player
 * Falls back to "Watch on YouTube" if embed is blocked
 * Controlled by parent - only one video plays at a time
 * 
 * @param {string} url - YouTube video URL
 * @param {string} title - Video title
 * @param {string[]} categories - Array of category names
 * @param {boolean} isPlaying - Controlled playing state from parent
 * @param {function} onPlay - Callback when play is clicked
 * @param {function} onStop - Callback when video is stopped
 */
const VideoCard = ({
  url,
  title = 'Video',
  categories = [],
  isPlaying = false,
  onPlay,
  onStop,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const iframeRef = useRef(null);
  const loadTimeoutRef = useRef(null);

  const youtubeVideoId = getYouTubeVideoId(url);
  const thumbnail = getYouTubeThumbnail(youtubeVideoId, 'hq');
  const embedUrl = getYouTubeEmbedUrl(youtubeVideoId, true);
  const directUrl = getYouTubeShortsUrl(youtubeVideoId);

  // Color mapping for different category types
  const categoryColors = {
    live: 'bg-primary/30 text-primary',
    acoustic: 'bg-gold/30 text-gold',
    recording: 'bg-neon-pink/30 text-neon-pink',
    private: 'bg-cyan-500/30 text-cyan-400',
    cover: 'bg-purple-500/30 text-purple-400',
    band: 'bg-yellow-500/30 text-yellow-400',
  };

  const defaultColor = 'bg-gray-500/30 text-gray-400';

  // Reset loading/error state when isPlaying changes
  useEffect(() => {
    if (isPlaying) {
      setIsLoading(true);
      setHasError(false);
      
      // Set a timeout to detect if video fails to load properly
      loadTimeoutRef.current = setTimeout(() => {
        setHasError(true);
        setIsLoading(false);
      }, 8000); // 8 second timeout
    } else {
      // Clean up when stopped
      setIsLoading(false);
      setHasError(false);
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    }
  }, [isPlaying]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, []);

  const handlePlay = () => {
    onPlay?.();
  };

  const handleIframeLoad = () => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }
    setIsLoading(false);
  };

  const handleOpenYouTube = () => {
    window.open(directUrl, '_blank', 'noopener,noreferrer');
  };

  const handleReset = () => {
    setHasError(false);
    setIsLoading(false);
    onStop?.();
  };

  return (
    <div className="group relative w-full overflow-hidden rounded-2xl bg-[#101823] shadow-lg ring-1 ring-white/5">
      {/* Video Container - 9:16 aspect ratio for Shorts */}
      <div className="relative aspect-[9/16] w-full overflow-hidden">
        {!isPlaying ? (
          <>
            {/* Thumbnail */}
            <img
              src={thumbnail}
              alt={title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            
            {/* Play Button */}
            <button
              onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center"
              aria-label="Play video"
            >
              <div className="flex size-14 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-300 group-hover:scale-110 group-hover:bg-primary/30 group-hover:border-primary/50">
                <Icon 
                  name="play_arrow" 
                  size="xl" 
                  className="ml-0.5 text-white group-hover:text-primary transition-colors" 
                  filled 
                />
              </div>
            </button>

            {/* Category Badges */}
            <div className="absolute top-3 left-3 flex flex-wrap gap-1">
              {categories.slice(0, 2).map((cat, index) => (
                <span 
                  key={index}
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm ${
                    categoryColors[cat.toLowerCase()] || defaultColor
                  }`}
                >
                  {cat}
                </span>
              ))}
            </div>
          </>
        ) : hasError ? (
          /* Error State - Show "Watch on YouTube" */
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/95 p-4">
            {/* Background thumbnail with blur */}
            <img
              src={thumbnail}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-20 blur-sm"
            />
            
            <div className="relative z-10 text-center">
              <Icon name="play_disabled" size="xl" className="text-gray-500 mb-3" />
              <p className="text-gray-400 text-xs mb-4">
                Video unavailable for embed
              </p>
              
              <button
                onClick={handleOpenYouTube}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-semibold rounded-full transition-colors mx-auto"
              >
                <Icon name="play_arrow" size="sm" filled />
                Watch on YouTube
              </button>
              
              <button
                onClick={handleReset}
                className="mt-3 text-gray-500 hover:text-gray-300 text-xs underline transition-colors"
              >
                Go back
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Loading State */}
            {isLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black z-10">
                <Icon name="progress_activity" size="xl" className="text-primary animate-spin mb-2" />
                <p className="text-gray-500 text-xs">Loading video...</p>
              </div>
            )}
            
            {/* YouTube Embed */}
            <iframe
              ref={iframeRef}
              src={embedUrl}
              title={title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onLoad={handleIframeLoad}
            />

            {/* Close/Back button overlay */}
            <button
              onClick={handleReset}
              className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white/70 hover:text-white transition-all"
              aria-label="Close video"
            >
              <Icon name="close" size="sm" />
            </button>
          </>
        )}
      </div>

      {/* Title Bar - Only show when not playing */}
      {!isPlaying && (
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
          <h3 className="text-white text-sm font-semibold leading-tight line-clamp-2">
            {title}
          </h3>
        </div>
      )}
    </div>
  );
};

export default VideoCard;
